import { useQuery } from '@tanstack/react-query';

import { api, getQueryProps, isDefined } from 'common';

import { type PredefinedFlowAnalyticEvent } from '../scenes/Agents/Flows/Analytics/types';
import { getEventCategoryDisplayName } from '../scenes/Agents/Flows/Analytics/utils';

type StandardAnalyticEventWithDetails = api.StandardAnalyticEvent.StandardAnalyticEvent & {
  categoryName: string;
};

export function useStandardAnalyticEvents() {
  const {
    data: standardCategoriesById,
    isPending,
    error: standardCategoriesError,
  } = useQuery({
    ...getQueryProps(api.client.StandardAnalyticEventCategory, 'findAll'),
    select: (categories) => {
      return categories.reduce<
        Record<string, api.StandardAnalyticEventCategory.StandardAnalyticEventCategory>
      >((acc, category) => {
        acc[category.id] = category;
        return acc;
      }, {});
    },
  });

  const {
    data: standardEventsById,
    isPending: isStandardEventsPending,
    error: standardEventsError,
  } = useQuery({
    ...getQueryProps(api.client.StandardAnalyticEvent, 'findAll'),
    enabled: !isPending,
    select: (events) => {
      return events.reduce<Record<string, StandardAnalyticEventWithDetails>>((acc, event) => {
        acc[event.id] = {
          ...event,
          categoryName: standardCategoriesById?.[event.categoryId]?.name ?? '',
        };
        return acc;
      }, {});
    },
  });

  const getEventDetails = (
    event: Partial<
      Pick<
        PredefinedFlowAnalyticEvent,
        'name' | 'description' | 'category' | 'standardAnalyticEventId'
      >
    >
  ) => {
    return {
      ...event,
      ...(isDefined(event.standardAnalyticEventId)
        ? {
            name: standardEventsById?.[event.standardAnalyticEventId]?.name,
            description: standardEventsById?.[event.standardAnalyticEventId]?.description,
            category: standardEventsById?.[event.standardAnalyticEventId]?.categoryName,
          }
        : {
            category: getEventCategoryDisplayName(event.category),
          }),
    };
  };

  return {
    standardCategoriesById,
    standardEventsById,
    getEventDetails,
    isPending: isStandardEventsPending,
    error: standardCategoriesError || standardEventsError,
  };
}
